/**
 * Waveform utilities for voice messages
 * Based on Telegram Web A implementation
 */

// Constants for waveform rendering
const HEIGHT = 23;
const SPIKE_WIDTH = 2;
const SPIKE_STEP = 4;
const SPIKE_RADIUS = 1;
const MIN_SPIKES = 25;
const MAX_SPIKES = 75;
const MAX_EMPTY_WAVEFORM_POINTS = 30;

/**
 * Decode 5-bit encoded waveform from Telegram API
 * Each 5 bits represents one sample (0-31)
 */
export function decodeWaveform(encoded: string): number[] {
  if (!encoded) {return [];}

  try {
    // Base64 decode
    const binaryString = atob(encoded);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Calculate number of 5-bit values
    const valuesCount = Math.floor((bytes.length * 8) / 5);
    const result: number[] = [];

    for (let i = 0; i < valuesCount; i++) {
      const byteIndex = Math.floor((i * 5) / 8);
      const bitShift = (i * 5) % 8;

      // Extract 5 bits (value 0-31)
      let value = bytes[byteIndex] >> bitShift;
      if (bitShift > 3 && byteIndex + 1 < bytes.length) {
        value |= bytes[byteIndex + 1] << (8 - bitShift);
      }
      result.push(value & 0x1F); // 0x1F = 31
    }

    return result;
  } catch {
    // Invalid base64 or other error — return empty array
    console.warn('Failed to decode waveform:', encoded);
    return [];
  }
}

/**
 * Generate random waveform for messages without waveform data
 */
export function generateEmptyWaveform(): number[] {
  const count = Math.floor(Math.random() * MAX_EMPTY_WAVEFORM_POINTS) + 10;
  return Array.from({ length: count }, () => Math.floor(Math.random() * 32));
}

/**
 * Exponential Moving Average (EMA) smoothing
 * Smooths out spikes and noise in waveform data
 * Alpha = 2 / (N + 1) where N is the period
 */
function applyEMA(data: number[], period: number = 3): number[] {
  if (data.length === 0) {return [];}

  const alpha = 2 / (period + 1);
  const result: number[] = [];
  let ema = data[0];
  result.push(ema);

  for (let i = 1; i < data.length; i++) {
    ema = data[i] * alpha + ema * (1 - alpha);
    result.push(ema);
  }

  return result;
}

/**
 * Interpolate waveform array to target spike count
 * Uses exponential moving average (EMA) for smooth interpolation
 */
export function interpolateArray(
  data: number[],
  targetLength: number
): { data: number[]; peak: number } {
  if (data.length === 0) {
    return { data: [], peak: 0 };
  }

  if (data.length === targetLength) {
    return { data, peak: Math.max(...data) };
  }

  // Apply EMA smoothing first for better interpolation
  const smoothedData = applyEMA(data, 2);

  const result: number[] = [];
  const step = smoothedData.length / targetLength;

  for (let i = 0; i < targetLength; i++) {
    const pos = i * step;
    const index = Math.floor(pos);
    const fraction = pos - index;

    // Linear interpolation with Catmull-Rom-like smoothing
    const p0 = smoothedData[Math.max(0, index - 1)];
    const p1 = smoothedData[index];
    const p2 = smoothedData[Math.min(smoothedData.length - 1, index + 1)];
    const p3 = smoothedData[Math.min(smoothedData.length - 1, index + 2)];

    // Cubic Hermite interpolation for smoother results
    const t = fraction;
    const t2 = t * t;
    const t3 = t2 * t;

    const a0 = -0.5 * t3 + t2 - 0.5 * t;
    const a1 = 1.5 * t3 - 2.5 * t2 + 1;
    const a2 = -1.5 * t3 + 2 * t2 + 0.5 * t;
    const a3 = 0.5 * t3 - 0.5 * t2;

    const value = a0 * p0 + a1 * p1 + a2 * p2 + a3 * p3;
    result.push(Math.max(0, Math.round(value)));
  }

  return { data: result, peak: Math.max(...result) };
}

/**
 * Calculate number of spikes based on duration
 */
export function calculateSpikesCount(durationSeconds: number): number {
  // 10 seconds is average voice message duration
  const durationFactor = Math.min(durationSeconds / 10, 1);
  return Math.round(MIN_SPIKES + (MAX_SPIKES - MIN_SPIKES) * durationFactor);
}

/**
 * Waveform theme colors
 */
export interface WaveformColors {
  fill: string;
  progressFill: string;
}

export function getWaveformColors(isOutgoing: boolean, isDark: boolean): WaveformColors {
  if (isDark) {
    // Тёмная тема
    if (isOutgoing) {
      // Наши сообщения (на primary/фиолетовом фоне)
      // Чёрные полоски для видимости на светлом фоне сообщения
      return {
        fill: 'rgba(0, 0, 0, 0.3)',
        progressFill: 'rgba(0, 0, 0, 0.85)',
      };
    } else {
      // Входящие сообщения (на тёмно-сером фоне)
      // Фиолетовые полоски как акцент
      return {
        fill: 'rgba(135, 116, 225, 0.4)',
        progressFill: '#8774E1',
      };
    }
  }
  // Светлая тема
  if (isOutgoing) {
    // Наши сообщения (на primary/зелёном или синем фоне)
    // Чёрные полоски для контраста
    return {
      fill: 'rgba(0, 0, 0, 0.3)',
      progressFill: 'rgba(0, 0, 0, 0.85)',
    };
  } else {
    // Входящие сообщения (на светло-сером фоне)
    // Тёмно-серые полоски
    return {
      fill: 'rgba(0, 0, 0, 0.2)',
      progressFill: 'rgba(0, 0, 0, 0.7)',
    };
  }
}

/**
 * Draw rounded rectangle (spike)
 */
function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * Render waveform to canvas
 */
export function renderWaveform(
  canvas: HTMLCanvasElement,
  spikes: number[],
  peak: number,
  progress: number,
  colors: WaveformColors
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx || spikes.length === 0) {return;}

  const width = spikes.length * SPIKE_STEP;
  const height = HEIGHT;

  // High DPI rendering
  const dpr = window.devicePixelRatio || 1;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  ctx.scale(dpr, dpr);

  // Clear canvas
  ctx.clearRect(0, 0, width, height);

  // Normalize peak
  const normalizedPeak = Math.max(1, peak);

  // Draw spikes
  spikes.forEach((value, i) => {
    const progressRatio = i / spikes.length;
    const isPlayed = progressRatio < progress;

    // Set opacity and color
    ctx.globalAlpha = isPlayed ? 1 : 0.5;
    ctx.fillStyle = isPlayed ? colors.progressFill : colors.fill;

    // Calculate spike height (min 2px)
    const spikeHeight = Math.max(2, (height - 4) * (value / normalizedPeak));
    const x = i * SPIKE_STEP;
    const y = (height - spikeHeight) / 2;

    // Draw rounded rectangle
    roundedRect(ctx, x, y, SPIKE_WIDTH, spikeHeight, SPIKE_RADIUS);
    ctx.fill();
  });

  // Reset alpha
  ctx.globalAlpha = 1;
}

/**
 * Process waveform data for rendering
 */
export function processWaveform(
  encodedWaveform: string | undefined,
  durationSeconds: number
): { spikes: number[]; peak: number } {
  // Decode or generate waveform
  let rawWaveform: number[];
  if (encodedWaveform) {
    rawWaveform = decodeWaveform(encodedWaveform);
    // If decoding failed, generate empty waveform
    if (rawWaveform.length === 0) {
      rawWaveform = generateEmptyWaveform();
    }
  } else {
    rawWaveform = generateEmptyWaveform();
  }

  // Calculate target spike count based on duration
  const targetSpikes = calculateSpikesCount(Math.max(1, durationSeconds));

  // Interpolate to target length
  const result = interpolateArray(rawWaveform, targetSpikes);

  // Ensure we always return valid data
  return {
    spikes: result.data || [],
    peak: result.peak || 1,
  };
}
